import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import { useTenantPath } from "@/lib/tenantPath";
import { useRole } from "@/lib/roles";
import WhatsappGateModal from "@/components/WhatsappGateModal";
import ShiftsPanel from "@/pages/ShiftsPanel";
import CarsPanel from "@/pages/CarsPanel";
import AddConfirmedModal from "@/components/AddConfirmedModal";
import EventStockCard from "@/components/EventStockCard";
import EventActivityCard from "@/components/EventActivityCard";
import { useMessageChoice } from "@/components/MessageChoiceDialog";
import { ArrowLeft, Pencil, Trash2, Send, Copy, UserPlus, X, CheckCircle2, Star, Loader2, Link2, Calendar, Receipt, Search, Paperclip, Download, Upload, FileText, RotateCcw, MessageCircle, AlertTriangle, Ban } from "lucide-react";
import { API } from "@/lib/api";
import { formatDatePT, formatTimeRange } from "@/lib/format";

const DEFAULT_MSG = "Olá {nome_curto}! Temos um evento para ti: {evento} a {data} em {local}. Confirmas disponibilidade no link abaixo. Obrigado!\n\n{link}";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tPath = useTenantPath();
  const { canEdit } = useRole();
  const [msgDialog, askMessage] = useMessageChoice();
  const [data, setData] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [showSendModal, setShowSendModal] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MSG);
  const [sending, setSending] = useState(false);
  const [waveSize, setWaveSize] = useState(5);
  const [recSearch, setRecSearch] = useState("");
  const [recLocationFilter, setRecLocationFilter] = useState("");
  const [recTagFilter, setRecTagFilter] = useState("");
  const [waStatus, setWaStatus] = useState(null); // fetched once for the gate modal
  const [showWaGate, setShowWaGate] = useState(false);
  const [metaDefaultTpl, setMetaDefaultTpl] = useState(null);
  const [resending, setResending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [shiftTotals, setShiftTotals] = useState(null);
  const [confirmMsgStatus, setConfirmMsgStatus] = useState({});
  const [allPromoters, setAllPromoters] = useState([]);
  const [showAddConfirmed, setShowAddConfirmed] = useState(false);
  const [overlapMap, setOverlapMap] = useState({});

  const loadAttachments = useCallback(() => {
    api.get(`/events/${id}/attachments`).then((r) => setAttachments(r.data)).catch(() => setAttachments([]));
  }, [id]);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { window.alert("Ficheiro demasiado grande (máx. 15 MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/events/${id}/attachments`, fd);
      loadAttachments();
    } catch (err) {
      window.alert("Erro no upload: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const downloadAttachment = (fileId, filename) => {
    const token = localStorage.getItem("labora_token");
    fetch(`${API}/events/${id}/attachments/${fileId}/download`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename || "anexo";
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const deleteAttachment = async (fileId) => {
    if (!window.confirm("Remover este anexo?")) return;
    await api.delete(`/events/${id}/attachments/${fileId}`).catch(() => {});
    loadAttachments();
  };

  const handleResend = async (target = "sem_resposta") => {
    if (!waConnected) { setShowWaGate(true); return; }
    const list = target === "sem_resposta" ? lists.sem_resposta : [...contactedIds].filter((pid) => !confirmedSet.has(pid) && !unavailableIds.has(pid) && !removedSet.has(pid));
    if (list.length === 0) { window.alert("Nenhum promotor elegível para reenvio."); return; }
    const label = target === "sem_resposta" ? "sem resposta" : "contactados";
    if (!window.confirm(`Reenviar WhatsApp a ${list.length} promotor(es) ${label}?`)) return;
    setResending(true);
    try {
      const r = await api.post(`/events/${id}/requests/resend`, { target });
      const { sent = 0, failed = 0, skipped = 0 } = r.data?.delivery || {};
      window.alert(`Reenvio em fila.\nEm fila: ${sent}\nFalhados: ${failed}\nIgnorados: ${skipped}\n\nO estado real (Enviado/Entregue/Lido) aparece nos badges de cada promotor.`);
      refresh();
    } catch (err) {
      window.alert("Erro no reenvio: " + (err?.response?.data?.detail || err.message));
    } finally {
      setResending(false);
    }
  };

  const refresh = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    Promise.all([
      api.get(`/events/${id}`),
      api.get(`/events/${id}/recommendations`).catch(() => ({ data: [] })),
      api.get(`/templates?channel=whatsapp`).catch(() => ({ data: [] })),
      api.get(`/events/${id}/message-status?kind=confirmacao`).catch(() => ({ data: {} })),
      api.get(`/events/${id}/overlap-warnings`).catch(() => ({ data: {} })),
    ])
      .then(([d, r, t, m, o]) => { setData(d.data); setRecommendations(r.data); setTemplates(t.data); setConfirmMsgStatus(m.data || {}); setOverlapMap(o.data?.event || {}); })
      .catch(() => {})
      .finally(() => { if (!silent) setLoading(false); });
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);
  // Auto-refresh so responses (accepted via link/WhatsApp) appear without reloading.
  useEffect(() => {
    const iv = setInterval(() => refresh(true), 15000);
    return () => clearInterval(iv);
  }, [refresh]);
  useEffect(() => { loadAttachments(); }, [loadAttachments]);
  useEffect(() => { api.get("/promoters").then((r) => setAllPromoters(r.data)).catch(() => setAllPromoters([])); }, []);

  // Fetch WhatsApp status once — used by the just-in-time gate on send actions.
  // Uses the secret-free /whatsapp/health endpoint (viewer+), so MANAGERS can
  // also see the connected state (the admin-only /whatsapp-settings returned 403
  // for gestores, which wrongly triggered the "connect WhatsApp" gate).
  useEffect(() => {
    api.get("/organizations/me/whatsapp/health")
      .then((r) => setWaStatus(r.data?.status || "not_connected"))
      .catch(() => setWaStatus("not_connected"));
  }, []);

  // The approved Meta template that is actually sent for invites = the registry
  // template marked default (active) for kind 'convite'. Shown in the send modal
  // so the operator sees exactly which Meta-approved message goes out.
  useEffect(() => {
    api.get("/wa-templates")
      .then((r) => {
        const rows = (r.data || []).filter((t) => t.kind === "convite" && t.status === "approved");
        const def = rows.find((t) => t.active) || rows[0] || null;
        setMetaDefaultTpl(def);
      })
      .catch(() => setMetaDefaultTpl(null));
  }, []);

  const waConnected = waStatus === "connected";
  const openSendModal = () => {
    if (!waConnected) { setShowWaGate(true); return; }
    setShowSendModal(true);
  };

  const promoterMap = useMemo(() => {
    if (!data) return {};
    return Object.fromEntries(data.promoters.map((p) => [p.id, p]));
  }, [data]);

  const allPromoterMap = useMemo(
    () => Object.fromEntries((allPromoters || []).map((p) => [p.id, p])),
    [allPromoters],
  );

  const requestsByPromoter = useMemo(() => {
    if (!data) return {};
    const map = {};
    data.requests.forEach((r) => { map[r.promoter_id] = r; });
    return map;
  }, [data]);

  const recLocations = useMemo(
    () => Array.from(new Set(recommendations.map((p) => p.location).filter(Boolean))).sort(),
    [recommendations],
  );
  const recTags = useMemo(
    () => Array.from(new Set(recommendations.flatMap((p) => p.tags || []))).sort(),
    [recommendations],
  );
  const filteredRecommendations = useMemo(() => {
    const q = recSearch.trim().toLowerCase();
    return recommendations.filter((p) => {
      if (q && !p.name.toLowerCase().includes(q)) return false;
      if (recLocationFilter && p.location !== recLocationFilter) return false;
      if (recTagFilter && !(p.tags || []).includes(recTagFilter)) return false;
      return true;
    });
  }, [recommendations, recSearch, recLocationFilter, recTagFilter]);

  if (loading || !data) return <div className="text-sm text-zinc-500">A carregar evento…</div>;

  const { event, client } = data;
  const confirmed = event.confirmed_ids.length;
  const missing = Math.max(0, event.slots_needed - confirmed);

  const handleDelete = async () => {
    if (!window.confirm("Eliminar este evento e todos os pedidos relacionados?")) return;
    await api.delete(`/events/${id}`);
    navigate(tPath("/eventos"));
  };

  const cancelEvent = async () => {
    let count = 0;
    try {
      const r = await api.get(`/events/${id}/cancel-recipients`);
      count = r.data?.count || 0;
    } catch { /* ignore */ }
    if (!window.confirm(`Cancelar este evento e avisar ${count} promotor(es) (confirmados + que aceitaram) por WhatsApp?`)) return;
    try {
      const r = await api.post(`/events/${id}/cancel`);
      const d = r.data || {};
      toast.success("Evento cancelado", {
        description: `${d.sent || 0} em fila para aviso por WhatsApp${d.skipped ? ` · ${d.skipped} sem WhatsApp/sem número` : ""}${d.failed ? ` · ${d.failed} falharam` : ""}.`,
      });
    } catch (e) {
      toast.error("Erro ao cancelar", { description: e?.response?.data?.detail || e.message });
    }
    refresh();
  };

  const toggleSelect = (pid) => {
    const next = new Set(selected);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    setSelected(next);
  };

  const selectAllRecommended = () => {
    const ids = filteredRecommendations.slice(0, Math.max(waveSize, missing * 2)).map((p) => p.id);
    setSelected(new Set(ids));
  };

  const selectNextWave = () => {
    const ids = filteredRecommendations.slice(0, waveSize).map((p) => p.id);
    setSelected(new Set(ids));
    if (!waConnected) { setShowWaGate(true); return; }
    setShowSendModal(true);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const payload = { promoter_ids: Array.from(selected), message };
      if (selectedTemplate) payload.template_id = selectedTemplate;
      await api.post(`/events/${id}/requests`, payload);
      setSelected(new Set());
      setShowSendModal(false);
      refresh();
    } finally {
      setSending(false);
    }
  };

  const applyTemplate = (tplId) => {
    setSelectedTemplate(tplId);
    if (!tplId) return;
    const tpl = templates.find((t) => t.id === tplId);
    if (tpl) setMessage(tpl.body);
  };

  const confirmPromoter = async (pid) => {
    const name = promoterMap[pid]?.name || allPromoters.find((p) => p.id === pid)?.name || "Promotor";
    const conflicts = overlapMap[pid];
    if (conflicts && conflicts.length > 0) {
      const lines = conflicts.map((c) => `• ${c.event_name} (${c.time}) — ${c.status === "confirmado" ? "confirmado" : "aceitou"}`).join("\n");
      if (!window.confirm(`⚠️ ${name} já tem horário sobreposto noutro evento:\n\n${lines}\n\nConfirmar mesmo assim?`)) return;
    }
    const choice = await askMessage({ eventId: id, promoterId: pid, promoterName: name, kind: "confirmacao", title: "Confirmar promotor", confirmLabel: "Confirmar" });
    if (!choice) return false;
    try {
      const r = await api.post(`/events/${id}/confirm/${pid}`, choice);
      const wa = r.data?.whatsapp || {};
      if (r.data?.as === "suplente") {
        toast.success(`${name} adicionado como suplente`, { description: "Vagas cheias — sem mensagem enviada." });
      } else if (!choice.send_message) {
        toast.success(`${name} confirmado`, { description: "Sem mensagem enviada ao promotor." });
      } else if (wa.ok) {
        toast.success(`${name} confirmado`, { description: "Confirmação em fila — será enviada por WhatsApp 📤" });
      } else if (wa.detail === "skipped" || !waConnected) {
        toast.warning(`${name} confirmado`, { description: "WhatsApp não ligado — mensagem de confirmação não enviada." });
      } else {
        toast.warning(`${name} confirmado`, { description: `Confirmação por WhatsApp não enviada (${wa.detail || "erro"}).` });
      }
    } catch (e) {
      toast.error("Erro ao confirmar", { description: e?.response?.data?.detail || e.message });
    }
    refresh();
  };

  const removePromoter = async (pid) => {
    const p = promoterMap[pid];
    const name = p?.name || "este promotor";
    const choice = await askMessage({ eventId: id, promoterId: pid, promoterName: name, kind: "dispensa", title: "Remover do evento (a vaga fica livre)", confirmLabel: "Remover" });
    if (!choice) return;
    await api.post(`/events/${id}/remove/${pid}`, { send_rejection: choice.send_message, registry_template_id: choice.registry_template_id, template_id: choice.template_id });
    refresh();
  };

  const dismissPromoter = async (pid) => {
    const p = promoterMap[pid];
    const name = p?.name || "este promotor";
    const choice = await askMessage({ eventId: id, promoterId: pid, promoterName: name, kind: "dispensa", title: "Dispensar (podes readmitir depois)", confirmLabel: "Dispensar" });
    if (!choice) return;
    await api.post(`/events/${id}/remove/${pid}`, { send_rejection: choice.send_message, registry_template_id: choice.registry_template_id, template_id: choice.template_id });
    refresh();
  };

  const readmitPromoter = async (pid) => {
    await api.post(`/events/${id}/readmit/${pid}`);
    refresh();
  };

  const copyPublicLink = (token) => {
    const url = `${window.location.origin}/p/${token}`;
    navigator.clipboard.writeText(url);
  };

  const downloadICS = () => {
    const token = localStorage.getItem("labora_token");
    fetch(`${API}/events/${id}/ics`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${event.name.replace(/[^a-z0-9]/gi, "_")}.ics`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const generateInvoices = async () => {
    const r = await api.post(`/events/${id}/generate-invoices`);
    if (r.data.count === 0) {
      window.alert(r.data.detail || "Sem recibos novos a gerar (já podem existir).");
    } else {
      window.alert(`${r.data.count} recibo(s) gerado(s) com sucesso.`);
    }
  };

  // Categorize for Recrutamento lists
  const contactedIds = new Set(event.contacted_ids);
  const availableIds = new Set(event.available_ids);
  const unavailableIds = new Set(event.unavailable_ids);
  const noResponseIds = new Set(event.no_response_ids);
  const confirmedSet = new Set(event.confirmed_ids);
  const suplentesSet = new Set(event.suplentes_ids);
  const removedSet = new Set(event.removed_ids);

  const lists = {
    confirmados: [...confirmedSet].filter((pid) => !removedSet.has(pid)),
    aceitaram: [...availableIds].filter((pid) => !confirmedSet.has(pid) && !removedSet.has(pid)),
    recusaram: [...unavailableIds].filter((pid) => !removedSet.has(pid)),
    sem_resposta: [...noResponseIds].filter((pid) => !availableIds.has(pid) && !unavailableIds.has(pid) && !removedSet.has(pid)),
    suplentes: [...suplentesSet].filter((pid) => !removedSet.has(pid)),
    dispensados: [...removedSet],
  };

  return (
    <div className="space-y-6" data-testid="event-detail-page">
      <Link to={tPath("/eventos")} className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900">
        <ArrowLeft className="w-4 h-4" /> Voltar a eventos
      </Link>

      {/* Header */}
      <div className="bg-white border border-zinc-200 rounded-xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <StateBadge status={event.status} />
              {client && <span className="text-xs text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-full">{client.name}</span>}
            </div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-zinc-950">{event.name}</h1>
            {event.code && <div className="text-xs font-mono text-zinc-500 mt-0.5" data-testid="event-code">{event.code}</div>}
            <div className="text-sm text-zinc-600 mt-1 inline-flex items-center gap-1.5 flex-wrap">
              <Calendar className="w-3.5 h-3.5 text-zinc-400" />
              <span>{formatDatePT(event.date)}</span>
              {event.start_time && <span>· {formatTimeRange(event.start_time, event.end_time)}</span>}
              {event.location && <span>· {event.location}</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={downloadICS} data-testid="download-ics-button" className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-200 rounded-lg text-sm hover:bg-zinc-50">
              <Calendar className="w-3.5 h-3.5" /> .ics
            </button>
            {(event.confirmed_ids.length > 0 || event.shifts_enabled) && canEdit && (
              <button onClick={generateInvoices} data-testid="generate-invoices-button" className="inline-flex items-center gap-1.5 px-3 py-2 border border-emerald-200 text-emerald-700 bg-emerald-50/50 rounded-lg text-sm hover:bg-emerald-100">
                <Receipt className="w-3.5 h-3.5" /> Gerar recibos
              </button>
            )}
            {canEdit && (
              <>
                <Link to={tPath(`/eventos/${id}/editar`)} data-testid="edit-event-button" className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-200 rounded-lg text-sm hover:bg-zinc-50">
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </Link>
                {event.status !== "cancelado" && event.status !== "concluido" && (
                  <button onClick={cancelEvent} data-testid="cancel-event-button" className="inline-flex items-center gap-1.5 px-3 py-2 border border-amber-300 text-amber-800 rounded-lg text-sm hover:bg-amber-50">
                    <Ban className="w-3.5 h-3.5" /> Cancelar evento
                  </button>
                )}
                <button onClick={handleDelete} data-testid="delete-event-button" className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-700 rounded-lg text-sm hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4 mt-6">
          <Mini label="Vagas" value={event.shifts_enabled && shiftTotals ? shiftTotals.capacity : event.slots_needed} />
          <Mini label="Confirmadas" value={event.shifts_enabled && shiftTotals ? shiftTotals.confirmed : confirmed} accent="text-emerald-700" />
          <Mini label="Em falta" value={event.shifts_enabled && shiftTotals ? Math.max(0, shiftTotals.capacity - shiftTotals.confirmed) : missing} accent={(event.shifts_enabled && shiftTotals ? (shiftTotals.capacity - shiftTotals.confirmed) : missing) > 0 ? "text-amber-700" : "text-emerald-700"} />
          <Mini label="Valor/h" value={event.hourly_rate ? `€${event.hourly_rate}` : "—"} />
          <Mini label="Deslocação" value={event.travel_allowance ? `€${event.travel_allowance}` : "—"} />
        </div>

        {(event.profile || event.dress_code || event.briefing) && (
          <div className="mt-6 pt-6 border-t border-zinc-100 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {event.profile && <InfoBlock label="Perfil">{event.profile}</InfoBlock>}
            {event.dress_code && <InfoBlock label="Dress code">{event.dress_code}</InfoBlock>}
            {event.briefing && <InfoBlock label="Briefing">{event.briefing}</InfoBlock>}
          </div>
        )}
      </div>

      {/* Anexos */}
      <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="attachments-section">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="text-xl font-display font-bold text-zinc-950 inline-flex items-center gap-2"><Paperclip className="w-4 h-4 text-zinc-500" /> Anexos</h2>
            <div className="text-sm text-zinc-500 mt-0.5">Briefings, PDFs ou imagens do evento (máx. 15 MB)</div>
          </div>
          {canEdit && (
            <label data-testid="upload-attachment" className="inline-flex items-center gap-1.5 border border-zinc-200 hover:bg-zinc-50 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "A carregar…" : "Carregar ficheiro"}
              <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
          )}
        </div>
        {attachments.length === 0 ? (
          <div className="text-sm text-zinc-400 py-4 text-center border border-dashed border-zinc-200 rounded-lg">Sem anexos.</div>
        ) : (
          <div className="divide-y divide-zinc-100 border border-zinc-100 rounded-lg overflow-hidden">
            {attachments.map((a) => (
              <div key={a.id} data-testid={`attachment-${a.id}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50">
                <FileText className="w-4 h-4 text-zinc-400 shrink-0" />
                <button onClick={() => downloadAttachment(a.id, a.original_filename)} className="flex-1 text-left text-sm text-zinc-800 hover:underline truncate" title={a.original_filename}>{a.original_filename}</button>
                <span className="text-xs text-zinc-400 shrink-0 hidden sm:inline">{a.size ? `${(a.size / 1024).toFixed(0)} KB` : ""}</span>
                <button onClick={() => downloadAttachment(a.id, a.original_filename)} data-testid={`download-attachment-${a.id}`} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600" title="Descarregar"><Download className="w-3.5 h-3.5" /></button>
                {canEdit && (
                  <button onClick={() => deleteAttachment(a.id)} data-testid={`delete-attachment-${a.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Remover"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recrutamento por turnos (quando ativo) */}
      {event.shifts_enabled && <ShiftsPanel eventId={id} canEdit={canEdit} onTotals={setShiftTotals} />}

      {/* Recrutamento */}
      {!event.shifts_enabled && (
      <div className="bg-white border border-zinc-200 rounded-xl p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="text-xl font-display font-bold text-zinc-950">Recrutamento</h2>
            <div className="text-sm text-zinc-500 mt-0.5">{confirmed}/{event.slots_needed} vagas preenchidas · {missing} em falta</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {lists.sem_resposta.length > 0 && (
              <button
                onClick={() => handleResend("sem_resposta")}
                disabled={resending}
                data-testid="resend-no-response"
                className="inline-flex items-center gap-1.5 border border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                title="Reenvia o WhatsApp aos promotores em 'Sem resposta'"
              >
                <Send className="w-3.5 h-3.5" /> {resending ? "A reenviar…" : `Reenviar (${lists.sem_resposta.length})`}
              </button>
            )}
            {missing > 0 && (
              <>
                <label className="text-xs text-zinc-600">Próximos</label>
                <input type="number" min="1" max="50" value={waveSize} onChange={(e) => setWaveSize(parseInt(e.target.value) || 1)} className="w-16 px-2 py-1 rounded border border-zinc-200 text-sm" data-testid="wave-size" />
                <button onClick={selectNextWave} data-testid="contact-next-wave" className="inline-flex items-center gap-1.5 bg-zinc-950 hover:bg-zinc-800 text-white px-3 py-2 rounded-lg text-sm font-semibold">
                  <Send className="w-3.5 h-3.5" /> Contactar próximos {waveSize}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Lists by state */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <PromoterList title="Confirmados" status="confirmado" ids={lists.confirmados} promoterMap={promoterMap} onAction={removePromoter} actionLabel="Remover" actionIcon={X} accent="danger" msgStatus={confirmMsgStatus} onAdd={canEdit ? () => setShowAddConfirmed(true) : null} overlapMap={overlapMap} />
          <PromoterList title="Aceitaram (a confirmar)" status="disponivel" ids={lists.aceitaram} promoterMap={promoterMap} onAction={confirmPromoter} actionLabel="Confirmar" actionIcon={CheckCircle2} accent="emerald" onSecondary={dismissPromoter} secondaryLabel="Dispensar" secondaryIcon={X} overlapMap={overlapMap} />
          <PromoterList title="Sem resposta" status="sem_resposta" ids={lists.sem_resposta} promoterMap={promoterMap} onAction={(pid) => copyPublicLink(requestsByPromoter[pid]?.token)} actionLabel="Copiar link" actionIcon={Link2} />
          <PromoterList title="Recusaram" status="indisponivel" ids={lists.recusaram} promoterMap={promoterMap} />
          <PromoterList title="Suplentes" status="disponivel" ids={lists.suplentes} promoterMap={promoterMap} onAction={confirmPromoter} actionLabel="Confirmar" actionIcon={CheckCircle2} onSecondary={dismissPromoter} secondaryLabel="Dispensar" secondaryIcon={X} />
          {lists.dispensados.length > 0 && (
            <PromoterList title="Dispensados" status="removido" ids={lists.dispensados} promoterMap={promoterMap} onAction={readmitPromoter} actionLabel="Readmitir" actionIcon={RotateCcw} accent="emerald" />
          )}
        </div>
      </div>
      )}

      <EventActivityCard eventId={id} promoterMap={{ ...allPromoterMap, ...promoterMap }} version={event} />

      {/* Fardamento & Equipamento */}
      <EventStockCard
        eventId={id}
        canEdit={canEdit}
        confirmedPromoters={event.shifts_enabled
          ? (shiftTotals?.confirmedPromoterIds || []).map((pid) => allPromoterMap[pid] || promoterMap[pid]).filter(Boolean)
          : (lists.confirmados || []).map((pid) => promoterMap[pid]).filter(Boolean)}
      />

      {/* Recommendations */}
      {!event.shifts_enabled && missing > 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-xl font-display font-bold text-zinc-950">Promotores recomendados</h2>
              <div className="text-sm text-zinc-500">{filteredRecommendations.length} de {recommendations.length} promotores ordenados por score</div>
            </div>
            <div className="flex gap-2">
              <button onClick={selectAllRecommended} data-testid="select-all-rec" className="text-sm text-zinc-700 hover:underline">Selecionar todos recomendados</button>
              <button onClick={() => setSelected(new Set())} className="text-sm text-zinc-500 hover:text-zinc-900">Limpar</button>
              <button onClick={openSendModal} disabled={selected.size === 0} data-testid="send-selected" className="inline-flex items-center gap-1.5 bg-zinc-950 hover:bg-zinc-800 text-white px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                <Send className="w-3.5 h-3.5" /> Enviar a {selected.size}
              </button>
            </div>
          </div>

          {/* Search & filters */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 pb-4 border-b border-zinc-100">
            <div className="relative sm:col-span-1">
              <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="rec-search"
                value={recSearch}
                onChange={(e) => setRecSearch(e.target.value)}
                placeholder="Pesquisar por nome…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
              />
            </div>
            <select data-testid="rec-location-filter" value={recLocationFilter} onChange={(e) => setRecLocationFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
              <option value="">Todas as localidades</option>
              {recLocations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select data-testid="rec-tag-filter" value={recTagFilter} onChange={(e) => setRecTagFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
              <option value="">Todas as tags</option>
              {recTags.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="overflow-auto max-h-[70vh] border border-zinc-100 rounded-lg" data-testid="rec-list-scroll">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-zinc-50 text-xs font-semibold text-zinc-500 uppercase tracking-wider border-b border-zinc-200">
                <tr>
                  <th className="text-left py-2 px-3 w-8"></th>
                  <th className="text-left py-2 px-3">Promotor</th>
                  <th className="text-left py-2 px-3 hidden md:table-cell">Local</th>
                  <th className="text-left py-2 px-3 hidden lg:table-cell">Tags</th>
                  <th className="text-right py-2 px-3">Ranking</th>
                  <th className="text-right py-2 px-3 hidden sm:table-cell">Score</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecommendations.map((p) => (
                  <tr key={p.id} data-testid={`rec-row-${p.id}`} onClick={() => toggleSelect(p.id)} className={`border-b border-zinc-100 cursor-pointer transition-colors ${selected.has(p.id) ? "bg-zinc-50" : "hover:bg-zinc-50"}`}>
                    <td className="py-2 px-3"><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} onClick={(e) => e.stopPropagation()} data-testid={`rec-check-${p.id}`} /></td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        {p.photo_url ? (
                          <img src={p.photo_url} alt={p.name} className="w-8 h-8 rounded-full object-cover bg-zinc-100" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-zinc-200 text-zinc-700 text-[10px] font-semibold flex items-center justify-center">{p.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</div>
                        )}
                        <div className="font-medium text-zinc-900">{p.name}</div>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-zinc-700 hidden md:table-cell">{p.location}</td>
                    <td className="py-2 px-3 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(p.tags || []).slice(0, 3).map((t) => <span key={t} className="text-[10px] uppercase tracking-wide font-medium text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-full">{t}</span>)}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right text-zinc-900 font-semibold"><span className="inline-flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{p.ranking?.toFixed(1)}</span></td>
                    <td className="py-2 px-3 text-right text-zinc-700 hidden sm:table-cell font-mono text-xs">{p.score}</td>
                  </tr>
                ))}
                {filteredRecommendations.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-sm text-zinc-500">{recommendations.length === 0 ? "Sem mais recomendações disponíveis." : "Sem promotores que correspondam aos filtros."}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Just-in-time gate: only when clicking a WhatsApp send action without connected WA */}
      <WhatsappGateModal open={showWaGate} onClose={() => setShowWaGate(false)} />
      {msgDialog}
      <AddConfirmedModal
        open={showAddConfirmed}
        onClose={() => setShowAddConfirmed(false)}
        promoters={allPromoters}
        excludeIds={new Set(lists.confirmados)}
        onAdd={confirmPromoter}
        title="Adicionar confirmado"
      />

      {/* Send Modal */}
      {showSendModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4" data-testid="send-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-zinc-950">Enviar pedidos</h2>
              <button onClick={() => setShowSendModal(false)} className="p-1 rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-sm text-zinc-600">A enviar a <span className="font-semibold text-zinc-900">{selected.size}</span> promotores. Será gerado um link único por promotor.</div>

            {templates.length > 0 && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">Template</label>
                <select data-testid="template-selector" value={selectedTemplate} onChange={(e) => applyTemplate(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
                  <option value="">— Mensagem personalizada —</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">Mensagem</label>
              <textarea data-testid="send-message" value={message} onChange={(e) => { setMessage(e.target.value); setSelectedTemplate(""); }} rows={7} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm font-mono" />
              <div className="text-[11px] text-zinc-500">Placeholders: {"{nome_curto}"}, {"{evento}"}, {"{data}"}, {"{horario}"}, {"{local}"}, {"{valor_hora}"}, {"{link}"}</div>
            </div>
            {waConnected ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-800 space-y-1" data-testid="wa-real-note">
                <div>✅ WhatsApp ligado — as mensagens serão enviadas realmente aos promotores (via Meta).</div>
                {metaDefaultTpl ? (
                  <div data-testid="wa-meta-default-tpl">
                    Template Meta aprovado a enviar: <span className="font-semibold">{metaDefaultTpl.name}</span>
                    {metaDefaultTpl.active ? " (predefinido)" : ""}.
                    <span className="text-emerald-700"> Para mudar o predefinido: Definições → WhatsApp → Templates aprovados.</span>
                  </div>
                ) : (
                  <div className="text-amber-700">⚠️ Sem template Meta aprovado para convites — define um em Definições → WhatsApp → Templates aprovados.</div>
                )}
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                ⚠️ WhatsApp em modo simulado. O envio cria pedidos e links únicos sem disparar mensagens reais.
              </div>
            )}
            <div className="flex gap-3 pt-2 border-t border-zinc-100">
              <button onClick={handleSend} disabled={sending} data-testid="confirm-send" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                Enviar pedidos
              </button>
              <button onClick={() => setShowSendModal(false)} className="px-5 py-2.5 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Gestão de boleias (quando ativo) */}
      {event.carpooling_enabled && (
        <div className="mt-2">
          <CarsPanel eventId={id} canEdit={canEdit} shiftsEnabled={event.shifts_enabled} />
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div>
      <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-bold tracking-tight font-display mt-1 ${accent || "text-zinc-950"}`}>{value}</div>
    </div>
  );
}

function InfoBlock({ label, children }) {
  return (
    <div>
      <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{children}</div>
    </div>
  );
}

function PromoterList({ title, status, ids, promoterMap, onAction, actionLabel, actionIcon: ActionIcon, accent, onSecondary, secondaryLabel, secondaryIcon: SecondaryIcon, msgStatus, onAdd, overlapMap }) {
  const tPath = useTenantPath();
  const accentCls = accent === "emerald" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : accent === "danger" ? "border border-red-200 text-red-700 hover:bg-red-50" : "border border-zinc-200 hover:bg-zinc-50";
  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden">
      <div className="px-4 py-2.5 bg-zinc-50/50 border-b border-zinc-100 flex items-center justify-between">
        <span className="text-sm font-semibold text-zinc-800">{title}</span>
        <div className="flex items-center gap-2">
          {onAdd && (
            <button onClick={onAdd} data-testid="add-confirmed-open" title="Adicionar confirmado diretamente" className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white">
              <UserPlus className="w-3 h-3" /> Adicionar
            </button>
          )}
          <span className="text-xs text-zinc-500">{ids.length}</span>
        </div>
      </div>
      <div className="divide-y divide-zinc-100 max-h-64 overflow-y-auto">
        {ids.length === 0 && <div className="px-4 py-6 text-center text-xs text-zinc-400">Sem promotores</div>}
        {ids.map((pid) => {
          const p = promoterMap[pid];
          if (!p) return null;
          return (
            <div key={pid} className="flex items-center gap-2 px-3 py-2">
              {p.photo_url ? (
                <img src={p.photo_url} alt="" className="w-7 h-7 rounded-full object-cover bg-zinc-100" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-zinc-200 text-zinc-700 text-[10px] font-semibold flex items-center justify-center">{p.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</div>
              )}
              <Link to={tPath(`/promotores/${pid}`)} className="flex-1 text-sm text-zinc-800 hover:underline truncate">{p.name}</Link>
              {overlapMap && overlapMap[pid] && overlapMap[pid].length > 0 && (
                <span
                  data-testid={`overlap-warning-${pid}`}
                  title={`Horário sobreposto: ${overlapMap[pid].map((c) => `${c.event_name} (${c.time}) — ${c.status === "confirmado" ? "confirmado" : "aceitou"}`).join(" · ")}`}
                  className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200 whitespace-nowrap"
                >
                  <AlertTriangle className="w-3 h-3" /> sobreposto
                </span>
              )}
              {msgStatus && <ConfirmationBadge info={msgStatus[pid]} pid={pid} />}
              {onSecondary && (
                <button onClick={() => onSecondary(pid)} data-testid={`dismiss-${status}-${pid}`} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50" title="Dispensar este promotor">
                  {SecondaryIcon && <SecondaryIcon className="w-3 h-3" />} {secondaryLabel}
                </button>
              )}
              {onAction && (
                <button onClick={() => onAction(pid)} data-testid={`action-${status}-${pid}`} className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded ${accentCls}`}>
                  {ActionIcon && <ActionIcon className="w-3 h-3" />} {actionLabel}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CONFIRM_BADGE = {
  read: { label: "Lido", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  delivered: { label: "Entregue", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  sent: { label: "Enviado", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  queued: { label: "A enviar…", cls: "bg-zinc-50 text-zinc-600 border-zinc-200" },
  failed: { label: "Falhou", cls: "bg-red-100 text-red-800 border-red-200" },
  blocked: { label: "Não enviado", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  expired: { label: "Expirada", cls: "bg-zinc-100 text-zinc-500 border-zinc-200" },
  skipped: { label: "Não enviado", cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

function ConfirmationBadge({ info, pid }) {
  if (!info || !info.status) {
    return (
      <span data-testid={`confirm-msg-${pid}`} title="Sem registo de mensagem de confirmação" className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border bg-zinc-50 text-zinc-400 border-zinc-200">
        Sem envio
      </span>
    );
  }
  const cfg = CONFIRM_BADGE[info.status] || CONFIRM_BADGE.sent;
  const title = info.error_message ? `WhatsApp: ${cfg.label} — ${info.error_message}` : `Confirmação WhatsApp: ${cfg.label}`;
  return (
    <span data-testid={`confirm-msg-${pid}`} title={title} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${cfg.cls}`}>
      <MessageCircle className="w-3 h-3" /> {cfg.label}
    </span>
  );
}
